/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import './HomepageMap.scss';

import * as React from 'react';
import { Icon } from 'sarsaparilla';

import {
    MapProvider,
    useMap,
    useMapMarkers,
    useMapPopup,
    useValidatedData,
    type MapProviderProps,
} from 'shared-ui/map';
import { useUserLocation } from 'shared-ui/geolocation';
import { SearchResultCard, formatLocationName } from 'shared-ui/search-result-card';

import { SearchResult } from '../api/search.types';

import { MapControls } from './MapControls';
import { useFiltersState } from '../FiltersContext';
import { useSearchOnMapMove } from './hooks/useSearchOnMapMove';

const MARKER_CLICK_CLASS = 'is-map-marker-click';
/** Must match the hit-testable layers in `useMapMarkers`. */
const MARKER_HIT_LAYERS = ['pin-layer', 'circle-layer'];

function trackHomepageMapEvent({ action, label }: { action: string; label: string }) {
    window.dataLayer?.push({
        event: 'map_interaction',
        category: 'Homepage',
        action,
        label,
    });
}

interface HomepageMapProps {
    data: SearchResult | undefined;
    isFetching?: boolean;
    isExpanded: boolean;
    setIsExpanded: React.Dispatch<React.SetStateAction<boolean>>;
}

/** Homepage default zoom, preserved from the legacy local Mapbox init. */
const HOMEPAGE_DEFAULT_ZOOM = 5;

/** Fallback map center (Washington, D.C.) when no search/user coordinates exist. */
const DEFAULT_CENTER: [number, number] = [-77.0369, 38.9072];

/**
 * Mapbox constructor options specific to the homepage map. Passed through the
 * shared `MapProvider`; the provider owns `container`/`style`/`center`/`zoom`.
 */
const HOMEPAGE_MAP_OPTIONS: MapProviderProps['mapOptions'] = {
    dragRotate: false,
    scrollZoom: true,
    cooperativeGestures: true,
};

export function HomepageMap({
    data,
    isFetching,
    isExpanded,
    setIsExpanded,
}: HomepageMapProps) {
    const userLocation = useUserLocation();
    const [filters] = useFiltersState();

    // The provider reads the initial center once on mount; compute it from the
    // same sources the legacy init used (filters → result payload → user location).
    const initialLat =
        filters.lat ||
        getCenterFromCorners(filters.latNe, filters.latSw) ||
        forceNumber(data?.latitude) ||
        userLocation?.latitude;

    const initialLng =
        filters.lng ||
        getCenterFromCorners(filters.lngNe, filters.lngSw) ||
        forceNumber(data?.longitude) ||
        userLocation?.longitude;

    const initialCenter = React.useMemo<[number, number]>(() => {
        if (
            typeof initialLat === 'number' &&
            !Number.isNaN(initialLat) &&
            typeof initialLng === 'number' &&
            !Number.isNaN(initialLng)
        ) {
            return [initialLng, initialLat];
        }
        return DEFAULT_CENTER;
    }, [initialLat, initialLng]);

    return (
        <MapProvider
            initialCenter={initialCenter}
            zoom={HOMEPAGE_DEFAULT_ZOOM}
            mapOptions={HOMEPAGE_MAP_OPTIONS}
        >
            <HomepageMapCanvas
                data={data}
                isFetching={isFetching}
                isExpanded={isExpanded}
                setIsExpanded={setIsExpanded}
            />
        </MapProvider>
    );
}

/**
 * Canvas child: consumes the shared map context and owns the homepage-specific
 * concerns (search-on-map-move, suggestion-bbox framing, re-fit on new location
 * search, active-marker selection, and expand/collapse resizing).
 */
function HomepageMapCanvas({
    data,
    isFetching,
    isExpanded,
    setIsExpanded,
}: HomepageMapProps) {
    const { mapContainerRef, mapErrorType, mapInstance, fitBoundsToMarkers } = useMap();
    const [filters] = useFiltersState();

    const [activeItemId, setActiveItemId] = React.useState<string | null>(null);

    // Mirror the active id in a ref so `handleMarkerClick` can read the current
    // selection without depending on `activeItemId`. Depending on it would give
    // the callback a new identity on every marker click, which makes
    // `useMapMarkers` tear down and re-register its layer-scoped mouseenter/
    // mouseleave cursor delegates mid-hover; Mapbox resets its internal
    // `mouse in` latch on re-register, so a fast pointer-off never fires
    // `clearPointer` and the canvas is left stuck on `cursor: pointer`.
    // A sync effect (rather than only writing the ref inside the click handler)
    // is required because `activeItemId` is also reset to `null` elsewhere (the
    // `hoveredCardId` effect and the popup close button), and the ref must stay
    // authoritative for those paths too.
    const activeItemIdRef = React.useRef<string | null>(null);
    React.useEffect(() => {
        activeItemIdRef.current = activeItemId;
    }, [activeItemId]);

    // Flag set immediately before any programmatic camera move so the resulting
    // `moveend` isn't mistaken for a user gesture by search-on-map-move.
    const skipNextMoveEndRef = React.useRef(false);

    // The shared hook performs the very first fit itself; track it so our
    // re-fit effect only fires on subsequent location searches.
    const didInitialFitRef = React.useRef(false);

    const validatedData = useValidatedData(data?.results);

    const handleMarkerClick = React.useCallback(
        (id: string | null) => {
            const prevId = activeItemIdRef.current;

            if (id && id !== prevId) {
                const item = validatedData.find((entry) => entry.id === id);
                if (item?.name) {
                    trackHomepageMapEvent({
                        action: 'Map Marker - Click',
                        label: `Location - ${formatLocationName(item.name)}`,
                    });
                }
            }

            setActiveItemId(prevId === id ? null : id);
        },
        [validatedData]
    );

    const activeItem = React.useMemo(
        () => validatedData.find((item) => item.id === activeItemId) ?? null,
        [validatedData, activeItemId]
    );

    // Only re-fit after a location search (point + radius). For bbox (pan/zoom)
    // results the user controls the map, so no fit.
    const isLocationSearch = filters.lat != null && filters.lng != null;

    const handleBeforeFitBounds = React.useCallback(() => {
        skipNextMoveEndRef.current = true;
    }, []);

    useMapMarkers({
        data: validatedData,
        onMarkerClick: handleMarkerClick,
        activeCardId: activeItemId,
        hoveredCardId: filters.hoveredCardId,
        onBeforeFitBounds: handleBeforeFitBounds,
    });

    const { MapPopup, closePopup } = useMapPopup({ activeItem });

    useSearchOnMapMove({ mapInstance, skipNextMoveEndRef });

    // Re-fit to markers on a new location search. The initial-data and resize
    // fits are owned by the shared `useMapMarkers` hook.
    React.useEffect(() => {
        if (!mapInstance) return;
        if (!validatedData.length) return;

        if (!didInitialFitRef.current) {
            // Consume the shared hook's initial fit without double-fitting.
            didInitialFitRef.current = true;
            return;
        }

        if (isLocationSearch) {
            fitBoundsToMarkers();
        }
    }, [validatedData, mapInstance, isLocationSearch, fitBoundsToMarkers]);

    // Suggestion-based bbox searches include a locationId and should reframe the
    // map to that area. Drag/pan bbox searches clear locationId and keep the
    // user-controlled view.
    React.useEffect(() => {
        if (!mapInstance) return;

        const { latNe, lngNe, latSw, lngSw, locationId, lat, lng } = filters;
        const hasSearchBbox =
            typeof latNe === 'number' &&
            typeof lngNe === 'number' &&
            typeof latSw === 'number' &&
            typeof lngSw === 'number';

        const shouldFitToSearchBbox =
            hasSearchBbox && locationId != null && lat == null && lng == null;

        if (!shouldFitToSearchBbox) {
            return;
        }

        skipNextMoveEndRef.current = true;
        mapInstance.fitBounds(
            [
                [lngSw, latSw],
                [lngNe, latNe],
            ],
            {
                padding: 40,
                maxZoom: 8,
            }
        );
    }, [filters, mapInstance]);

    // Close popup when a card in the results list is hovered so its marker can
    // become active.
    React.useEffect(() => {
        if (filters.hoveredCardId) {
            setActiveItemId(null);
        }
    }, [filters.hoveredCardId]);

    // Resize the map canvas when expand/collapse changes layout so Mapbox
    // matches the new container size.
    React.useEffect(() => {
        if (!mapInstance) return;
        const frameId = requestAnimationFrame(() => {
            mapInstance.resize();
        });
        return () => cancelAnimationFrame(frameId);
    }, [isExpanded, mapInstance]);

    // GTM cannot tell a marker click from a canvas click. Tag the canvas so a
    // GTM exception can ignore marker presses (`Click Classes` contains this).
    React.useEffect(() => {
        if (!mapInstance) return;
        const canvas = mapInstance.getCanvas();

        const handlePointerDown = (event: PointerEvent) => {
            const rect = canvas.getBoundingClientRect();
            let isMarker = false;
            try {
                isMarker =
                    mapInstance.queryRenderedFeatures(
                        [event.clientX - rect.left, event.clientY - rect.top],
                        { layers: MARKER_HIT_LAYERS }
                    ).length > 0;
            } catch {
                isMarker = false;
            }
            canvas.classList.toggle(MARKER_CLICK_CLASS, isMarker);
        };

        canvas.addEventListener('pointerdown', handlePointerDown, true);
        return () => canvas.removeEventListener('pointerdown', handlePointerDown, true);
    }, [mapInstance]);

    if (mapErrorType) {
        return (
            <div className="nav--explore-destinations-map">
                {mapErrorType === 'webgl' ? (
                    <div className="search-map-error">
                        <Icon iconName="warning" className="error-icon" />
                        <h2 className="heading-sm m-1">WebGL is required</h2>
                        <p className="text-md">
                            WebGL is required to display maps on {process.env.SITE_NAME}.
                            Please enable WebGL or use a device / browser that supports it
                            to view the map.
                        </p>
                    </div>
                ) : (
                    <div className="search-map-error">
                        <Icon iconName="warning" className="error-icon" />
                        <h2 className="heading-sm m-1">
                            Something went wrong while loading the map
                        </h2>
                        <p className="text-md">
                            Please refresh the page or try again in a moment.
                        </p>
                    </div>
                )}
            </div>
        );
    }

    return (
        // id is used by GTM to target homepage map canvas clicks
        <div className="nav--explore-destinations-map" id="explore-destinations-map">
            <div ref={mapContainerRef} id="map-container" />

            <MapControls
                mapInstance={mapInstance}
                onExpandClick={() => {
                    setIsExpanded((prev) => !prev);
                }}
                isExpanded={isExpanded}
                isFetching={isFetching}
            />

            {activeItem ? (
                <MapPopup>
                    <SearchResultCard
                        item={activeItem}
                        isActive
                        hasCloseButton
                        shouldFocusOnMount
                        onCloseClick={() => {
                            closePopup();
                            setActiveItemId(null);
                        }}
                    />
                </MapPopup>
            ) : null}
        </div>
    );
}

/**
 * Coerces a value to a number if it is a number or a numeric string; otherwise returns null.
 */
function forceNumber(value: unknown): number | null {
    if (typeof value === 'number' && !Number.isNaN(value)) return value;
    if (typeof value === 'string') {
        const n = Number(value);
        return Number.isNaN(n) ? null : n;
    }
    return null;
}

function getCenterFromCorners(corner1: unknown, corner2: unknown) {
    const number1 = forceNumber(corner1);
    const number2 = forceNumber(corner2);
    if (number1 === null || number2 === null) {
        return null;
    }
    return (number1 + number2) / 2;
}

// cSpell:ignore criterias, moveend
